import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css } from 'styled-components'

import {
  customFormat,
  DATE_VALUES,
  dateFormats,
  formateDesignSystemDate,
  getRangeFromDateFilter,
} from 'utils/dateUtils'
import { useMount } from 'utils/hooks'

import {
  GET_METRIC_BANNER_ASYNC,
  GET_MONITORING_STATUS_INFO_ASYNC,
  SCHEDULE_STATUSES_LABELS,
  SCHEDULE_STATUSES_VALUES,
} from 'containers/SpearPhishingAgent/constants'
import { selectSpearPhishingAgentInfo } from 'containers/SpearPhishingAgent/selectors'

import { ArrowDown, FillCalendar, FillRadar } from 'components/lib/DesignSystem/Icons'
import Badge, { SHAPES, SIZES } from 'components/lib/DesignSystem/V3/Badge'
import { PSIMA_PENDO_FEATURES } from 'components/pages/SpearPhishingAgent/psimaPendo'

import AwarenessCoveragePlaceholder from './TopBanner/components/AwarenessCoveragePlaceholder'
import BehavioralRiskPlaceholder from './TopBanner/components/BehavioralRiskPlaceholder'
import ExternalHighRiskExposurePlaceholder from './TopBanner/components/ExternalHighRiskExposurePlaceholder'

export const TOP_BANNER_PLACEHOLDER_IDS = {
  AWARENESS_COVERAGE: 'awarenessCoverage',
  EXTERNAL_HIGH_RISK_EXPOSURE: 'externalHighRiskExposure',
  BEHAVIORAL_RISK: 'behavioralRisk',
}

export const TOP_BANNER_PLACEHOLDER_FILTERS = {
  [TOP_BANNER_PLACEHOLDER_IDS.AWARENESS_COVERAGE]: {
    label: 'Uncovered employees last 90D',
  },
  [TOP_BANNER_PLACEHOLDER_IDS.EXTERNAL_HIGH_RISK_EXPOSURE]: {
    label: 'OSINT threats last 7D',
  },
  [TOP_BANNER_PLACEHOLDER_IDS.BEHAVIORAL_RISK]: {
    label: 'Behavioral risk last 7D',
  },
}

const getLast7DaysRangeParams = () => {
  const [startIso, endIso] = getRangeFromDateFilter(DATE_VALUES.WEEK)

  return {
    startDate: customFormat(new Date(startIso), dateFormats.numbers),
    endDate: customFormat(new Date(endIso), dateFormats.numbers),
  }
}

export const TOP_BANNER_PLACEHOLDER_API_FILTERS = {
  [TOP_BANNER_PLACEHOLDER_IDS.AWARENESS_COVERAGE]: { minUncoveredPct: 60 },
  [TOP_BANNER_PLACEHOLDER_IDS.EXTERNAL_HIGH_RISK_EXPOSURE]: { ...getLast7DaysRangeParams() },
  [TOP_BANNER_PLACEHOLDER_IDS.BEHAVIORAL_RISK]: { minBehavioralRiskPct: 50 },
}

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  margin-bottom: 20px;
  border-radius: var(--t-border-radius-xl);
  overflow: hidden;
  background: var(--agent-hero-bg);
  border: 1px solid var(--agent-hero-border);
`

const HeroGlow = styled.div`
  background: var(--t-opacity-primary-40);
  border-radius: 977px;
  bottom: -106px;
  filter: blur(62px);
  height: 200px;
  pointer-events: none;
  position: absolute;
  right: -370px;
  transform: rotate(21.5deg);
  width: 977px;
  z-index: 0;
`

const Header = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 1150px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const Title = styled.h1`
  color: var(--t-colors-base-white);
  font-size: var(--t-typography-font-size-2xl);
  font-weight: var(--t-typography-font-weight-medium);
  line-height: var(--t-typography-line-height-2xl);
  margin: 0;
`

const Subtitle = styled.div`
  color: var(--t-colors-gray-400);
  font-size: var(--t-typography-font-size-sm);
  font-weight: var(--t-typography-font-weight-regular);
  line-height: var(--t-typography-line-height-sm);
  margin: var(--t-spacing-2) 0 0;
  max-width: 575px;
`

const ScheduleCard = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 3px 12px;
  min-height: 52px;
  background: var(--t-opacity-neutral-10);
  border-radius: var(--t-borders-radius-rounded-lg);
`

const ScheduleMainInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 182px;
`

const Divider = styled.div`
  width: 1px;
  height: 31px;
  background: var(--t-opacity-white-30);
`

const ScheduleItemContent = styled.div`
  display: flex;
  flex-direction: column;
`

const ScheduleItemTitle = styled.div`
  color: var(--t-colors-gray-400);
  font-size: 10px;
  line-height: 16px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
`

const ScheduleItemValue = styled.div`
  color: var(--t-colors-base-white);
  font-size: var(--t-typography-font-size-sm);
  line-height: var(--t-typography-line-height-sm);

  ${({ $loading }) =>
    $loading &&
    css`
      filter: blur(4px);
      opacity: 0.6;
      pointer-events: none;
      user-select: none;
      transition:
        filter 150ms ease,
        opacity 150ms ease;
    `}
`

const StatusBadge = styled(Badge)`
  align-self: center;
  font-weight: var(--t-typography-font-weight-medium);

  ${({ $loading }) =>
    $loading &&
    css`
      filter: blur(4px);
      opacity: 0.6;
      pointer-events: none;
      user-select: none;
      transition:
        filter 150ms ease,
        opacity 150ms ease;
    `}
`

const BlocksRow = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 820px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

const FilterByBadge = styled(Badge)`
  position: absolute;
  left: 50%;
  bottom: -14px;
  z-index: 10;
  height: 28px;
  padding: var(--t-spacing-1) var(--t-spacing-4);
  gap: var(--t-spacing-1);
  transform: translateX(-50%);
  opacity: 0;
  pointer-events: none;
  transition: opacity 180ms ease;
  font-weight: var(--t-typography-font-weight-medium);
  line-height: var(--t-typography-line-height-sm);
`

const BlockWrapper = styled.div`
  position: relative;
  overflow: visible;
  min-width: 0;
  padding: 20px;
  border-radius: var(--t-borders-radius-rounded-lg);
  background: var(--t-opacity-neutral-10);
  cursor: pointer;
  box-shadow: inset 0 0 0 2px transparent;
  transition:
    background 180ms ease,
    box-shadow 180ms ease,
    filter 180ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    box-shadow:
      inset 0 0 0 2px rgba(48, 99, 253, 0.6),
      0 10px 15px 0 rgba(0, 0, 0, 0.1),
      0 4px 6px 0 rgba(0, 0, 0, 0.1);
  }

  &:hover ${FilterByBadge} {
    opacity: 1;
  }

  ${({ $active }) =>
    $active &&
    css`
      background: rgba(255, 255, 255, 0.1);
      box-shadow:
        inset 0 0 0 2px rgba(48, 99, 253, 0.6),
        0 10px 15px 0 rgba(0, 0, 0, 0.1),
        0 4px 6px 0 rgba(0, 0, 0, 0.1);
    `}
`

const STATUSES_TEXT_COLORS_MAP = {
  [SCHEDULE_STATUSES_VALUES.ACTIVE]: 'var(--t-colors-green-500)',
  [SCHEDULE_STATUSES_VALUES.INACTIVE]: 'var(--t-colors-gray-500)',
}

const STATUSES_DOT_COLORS_MAP = {
  [SCHEDULE_STATUSES_VALUES.ACTIVE]: 'var(--t-colors-green-500)',
  [SCHEDULE_STATUSES_VALUES.INACTIVE]: 'var(--t-colors-gray-500)',
}

const STATUSES_BACKGROUND_COLORS_MAP = {
  [SCHEDULE_STATUSES_VALUES.ACTIVE]: 'var(--t-opacity-success-10)',
  [SCHEDULE_STATUSES_VALUES.INACTIVE]: 'var(--t-opacity-neutral-10)',
}

const TopBanner = ({ activePlaceholderId, onPlaceholderClick }) => {
  const dispatch = useDispatch()
  const scheduleInfo = useSelector(selectSpearPhishingAgentInfo)

  useMount(() => {
    dispatch(GET_MONITORING_STATUS_INFO_ASYNC.initiate())
    dispatch(GET_METRIC_BANNER_ASYNC.initiate())
  })

  return (
    <Container data-testid="Psima_Dashboard_Top_Banner">
      <HeroGlow />
      <Header>
        <div>
          <Title data-testid="Psima_Dashboard_Top_Banner_Title">Phishing Simulation Agent</Title>
          <Subtitle data-testid="Psima_Dashboard_Top_Banner_Subtitle">
            AI-generated simulations based on real intelligence about your organization.
          </Subtitle>
        </div>
        <ScheduleCard data-testid="Psima_Dashboard_Top_Banner_Schedule_Card">
          <ScheduleMainInfo>
            <FillRadar width="16px" height="16px" fill="var(--t-colors-base-white)" />
            <ScheduleItemContent>
              <ScheduleItemTitle data-testid="Psima_Dashboard_Last_OSINT_Scan_Label">
                Last OSINT Scan
              </ScheduleItemTitle>
              <ScheduleItemValue
                data-testid="Psima_Dashboard_Last_OSINT_Scan"
                $loading={scheduleInfo.loader}
              >
                {formateDesignSystemDate(scheduleInfo?.data?.lastUpdate, {
                  withYear: true,
                  monthFirst: true,
                  withMinutes: true,
                  fallback: '—',
                })}
              </ScheduleItemValue>
            </ScheduleItemContent>
          </ScheduleMainInfo>
          <Divider />
          <ScheduleMainInfo>
            <FillCalendar width="16px" height="16px" fill="var(--t-colors-base-white)" />
            <ScheduleItemContent>
              <ScheduleItemTitle data-testid="Psima_Dashboard_Next_OSINT_Schedule_Label">
                Next OSINT Schedule
              </ScheduleItemTitle>
              <ScheduleItemValue
                data-testid="Psima_Dashboard_Next_OSINT_Schedule"
                $loading={scheduleInfo.loader}
              >
                {formateDesignSystemDate(scheduleInfo?.data?.nextSchedule, {
                  withYear: true,
                  monthFirst: true,
                  withMinutes: true,
                  fallback: '—',
                })}
              </ScheduleItemValue>
            </ScheduleItemContent>
          </ScheduleMainInfo>
          <Divider />
          <StatusBadge
            dataTestId="Psima_Dashboard_Schedule_Status"
            size={SIZES.MEDIUM}
            shape={SHAPES.CAPSULE}
            color={STATUSES_BACKGROUND_COLORS_MAP[scheduleInfo.data.status]}
            textColor={STATUSES_TEXT_COLORS_MAP[scheduleInfo.data.status]}
            status={STATUSES_DOT_COLORS_MAP[scheduleInfo.data.status]}
            style={{ padding: 'var(--t-spacing-1-5) var(--t-spacing-3)', height: '32px' }}
            $loading={scheduleInfo.loader}
          >
            {SCHEDULE_STATUSES_LABELS[scheduleInfo.data.status]}
          </StatusBadge>
        </ScheduleCard>
      </Header>
      <BlocksRow data-testid="Psima_Dashboard_Top_Banner_Placeholders">
        <BlockWrapper
          $active={activePlaceholderId === TOP_BANNER_PLACEHOLDER_IDS.AWARENESS_COVERAGE}
          onClick={() => onPlaceholderClick(TOP_BANNER_PLACEHOLDER_IDS.AWARENESS_COVERAGE)}
          data-testid="Psima_Dashboard_Top_Banner_Placeholder_AwarenessCoverage"
          data-pendo-feature={PSIMA_PENDO_FEATURES.DASHBOARD_REMEDIATION_FILTER}
        >
          <AwarenessCoveragePlaceholder />
          <FilterByBadge
            dataTestId="Psima_Dashboard_Top_Banner_Placeholder_AwarenessCoverage_HoverFilter"
            size={SIZES.MEDIUM}
            shape={SHAPES.CAPSULE}
            color="var(--t-colors-blue-500)"
            textColor="var(--t-colors-blue-200)"
            rightElement={
              <ArrowDown width="16px" height="16px" fill="var(--t-colors-blue-200)" aria-hidden />
            }
          >
            Filter by
          </FilterByBadge>
        </BlockWrapper>
        <BlockWrapper
          $active={activePlaceholderId === TOP_BANNER_PLACEHOLDER_IDS.EXTERNAL_HIGH_RISK_EXPOSURE}
          onClick={() => onPlaceholderClick(TOP_BANNER_PLACEHOLDER_IDS.EXTERNAL_HIGH_RISK_EXPOSURE)}
          data-testid="Psima_Dashboard_Top_Banner_Placeholder_ExternalHighRiskExposure"
          data-pendo-feature={PSIMA_PENDO_FEATURES.DASHBOARD_REMEDIATION_FILTER}
        >
          <ExternalHighRiskExposurePlaceholder />
          <FilterByBadge
            dataTestId="Psima_Dashboard_Top_Banner_Placeholder_ExternalHighRiskExposure_HoverFilter"
            size={SIZES.MEDIUM}
            shape={SHAPES.CAPSULE}
            color="var(--t-colors-blue-500)"
            textColor="var(--t-colors-blue-200)"
            rightElement={
              <ArrowDown width="16px" height="16px" fill="var(--t-colors-blue-200)" aria-hidden />
            }
          >
            OSINT last 7 days
          </FilterByBadge>
        </BlockWrapper>
        <BlockWrapper
          $active={activePlaceholderId === TOP_BANNER_PLACEHOLDER_IDS.BEHAVIORAL_RISK}
          onClick={() => onPlaceholderClick(TOP_BANNER_PLACEHOLDER_IDS.BEHAVIORAL_RISK)}
          data-testid="Psima_Dashboard_Top_Banner_Placeholder_BehavioralRisk"
          data-pendo-feature={PSIMA_PENDO_FEATURES.DASHBOARD_REMEDIATION_FILTER}
        >
          <BehavioralRiskPlaceholder />
          <FilterByBadge
            dataTestId="Psima_Dashboard_Top_Banner_Placeholder_BehavioralRisk_HoverFilter"
            size={SIZES.MEDIUM}
            shape={SHAPES.CAPSULE}
            color="var(--t-colors-blue-500)"
            textColor="var(--t-colors-blue-200)"
            rightElement={
              <ArrowDown width="16px" height="16px" fill="var(--t-colors-blue-200)" aria-hidden />
            }
          >
            Filter by
          </FilterByBadge>
        </BlockWrapper>
      </BlocksRow>
    </Container>
  )
}

export default TopBanner
